// src/contexts/OptimizedContextProvider.tsx
"use client";

import React, { ReactNode, useMemo } from 'react';
import { User } from 'firebase/auth';
import type { Document, SelectedItem } from '@/types';
import type { AccessControl } from '@/lib/firebase';
import { CoreAppProvider } from './CoreAppProvider';
import { MessageStreamingProvider } from './MessageStreamingProvider';
import { ToolConfirmationProvider } from './ToolConfirmationProvider';

interface OptimizedContextProviderProps {
  children: ReactNode;
  assistantId: string;
  assistantConfig: {
    name: string;
    avatar: string;
    initialMessage?: string;
    initialInstructions: string;
    initialDocuments: Document[];
    tools: string[];
    toolConfigs: Record<string, Record<string, any>>;
    templateId?: string;
    accessControl?: AccessControl;
    adminEmail?: string;
    senderName?: string;
  };
  currentUser: User | null;
  selectedItems: SelectedItem[];
}

/**
 * Optimized context provider that reduces re-renders by:
 * 1. Using focused contexts with specific responsibilities
 * 2. Proper memoization to prevent unnecessary re-renders
 * 3. Service layer extraction for business logic
 * 4. Reduced context nesting levels
 */
export const OptimizedContextProvider: React.FC<OptimizedContextProviderProps> = ({
  children,
  assistantId,
  assistantConfig,
  currentUser,
  selectedItems
}) => {
  return (
    <CoreAppProvider
      assistantId={assistantId}
      assistantConfig={assistantConfig}
    >
      <MessageStreamingProvider
        assistantId={assistantId}
        assistantConfig={assistantConfig}
        currentUser={currentUser}
        selectedItems={selectedItems}
      >
        <ToolConfirmationProvider>
          {children}
        </ToolConfirmationProvider>
      </MessageStreamingProvider>
    </CoreAppProvider>
  );
};